// Maintain a registry of model instances that want to have changes to
// attribute values synchronized optimistically in the client.
//
// We use WeakRef here so that if there are no references to the model other
// than this code, then it can be garbage collected.

import WeakRef from '../../javascripts/WeakRef';
import type ApplicationModel from './ApplicationModel';

export default class SynchronizedModelRegistry {
  static globalRegistry: SynchronizedModelRegistry | null = null;

  models: Record<string, Set<WeakRef<ApplicationModel>>> = {};

  static get instance(): SynchronizedModelRegistry {
    const registry = this.globalRegistry || new SynchronizedModelRegistry();
    if (!this.globalRegistry) {
      this.globalRegistry = registry;
    }
    return registry;
  }

  // Register a model instance for synchronization.
  register(model: ApplicationModel) {
    if (!model.id) return;

    this.models[this.modelKey(model)] =
      this.models[this.modelKey(model)] || new Set();
    this.models[this.modelKey(model)].add(new WeakRef(model)); // eslint-disable-line no-undef
  }

  modelKey(model: ApplicationModel) {
    return `${model.typename}-${model.id}`;
  }

  // Synchronize a change from model to all other instances of the same record.
  synchronize(model: ApplicationModel, changedAttributes: Record<string, any>) {
    for (const ref of this.models[this.modelKey(model)] || []) {
      const instance = ref.deref();
      if (!instance) continue;

      for (const attribute in changedAttributes) {
        if (instance.modelConstructor.hasField(attribute)) {
          instance[attribute] = changedAttributes[attribute];
        }
      }
    }
  }
}
